// SCR-233 / SCR-595 (REQ-242): an org's notification webhooks, on both shells — name and host,
// format, events, status (failing first, highlighted), last success, last failure and consecutive
// failures. A row opens its summary with the delivery log and the actions: Edit, Rotate secret (the
// new secret is shown once), Send test (shows what happened), Enable/Disable and Delete. "New
// webhook" creates one and shows its secret once; the eleventh is refused with the limit message.
// A 501 reads "not available". While the proxy's API is unreachable (ADR-104) the screen offers no
// write control and says why. Where it sits is the shell's: the billing admin's customer
// Notifications tab, the proxy's Admin → Notifications.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import type { OrgContext } from "../../api/org-context";
import { useReadOnly } from "../../host/readonly";
import { Card, Chip } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { StaleNote } from "../../primitives/stale-note";
import { Empty } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import type { ScopeMe } from "../../scope/scope";
import { loadable, type Loadable } from "../quotas/model";
import { DeliveriesList } from "./deliveries";
import { ConfirmedAction, SecretRevealDialog, WebhookForm } from "./webhook-dialog";
import {
  atLimit,
  canWriteWebhooks,
  deleteWebhook,
  LIMIT_MESSAGE,
  loadWebhooks,
  NOT_AVAILABLE,
  ownerChoices,
  patchWebhook,
  revealSecret,
  rotateSecret,
  statusToggle,
  testOutcome,
  testWebhook,
  webhookRow,
  webhookRows,
  type NotificationWebhook,
  type NotificationWebhookWithSecret,
  type SecretReveal,
  type TestOutcome,
  type WebhookFormMode,
  type WebhookRow,
} from "./model";

const COLUMNS: readonly Column<WebhookRow>[] = [
  {
    key: "name",
    header: "Webhook",
    cell: (row) => (
      <span class="stack-cell">
        <span>{row.name}</span>
        <span class="cell-sub">{row.host}</span>
      </span>
    ),
  },
  { key: "format", header: "Format", help: "notifications.format", cell: (row) => row.format },
  { key: "events", header: "Events", help: "notifications.events", cell: (row) => <span class="webhook-events">{row.events}</span> },
  { key: "status", header: "Status", help: "notifications.status", cell: (row) => <Chip tone={row.statusTone}>{row.statusLabel}</Chip> },
  { key: "lastSuccess", header: "Last success", cell: (row) => row.lastSuccess },
  { key: "lastFailure", header: "Last failure", cell: (row) => row.lastFailure },
  {
    key: "failures",
    header: "Failures in a row",
    help: "notifications.consecutiveFailures",
    numeric: true,
    cell: (row) => <span class={row.consecutiveFailures > 0 ? "webhook-failures" : undefined}>{row.consecutiveFailures}</span>,
  },
];

type Action = "rotate" | "test" | "toggle" | "delete";

const ACTION_TEXT: Readonly<Record<Action, { label: string; message: string }>> = {
  rotate: { label: "Rotate secret", message: "A new signing secret replaces the current one at once; deliveries from now on are signed with it. Update the receiver right after." },
  test: { label: "Send test", message: "Sends one synthetic quota.test event (no money, no prompt content) to the webhook now, and records the attempt in its health." },
  toggle: { label: "", message: "" },
  delete: { label: "Delete", message: "The webhook, its stored URL and secret and its pending deliveries are removed. This cannot be undone." },
};

function toggleMessage(webhook: NotificationWebhook): string {
  return webhook.status === "active" ? "Nothing is sent to this webhook until it is enabled again." : "Deliveries resume with the next event; the failure count starts over.";
}

export interface WebhooksTabProps {
  context: OrgContext;
  /** Who acts: an org admin, a super-admin or a platform principal (who changes none). */
  me: ScopeMe;
}

/** The webhooks of one org, with the dialogs for each row. */
export function WebhooksTab({ context, me }: WebhooksTabProps) {
  const client = useApi();
  const { readOnly } = useReadOnly();
  const [state, setState] = useState<Loadable<NotificationWebhook[]>>({ kind: "loading" });
  const [open, setOpen] = useState<WebhookRow | null>(null);
  const [form, setForm] = useState<WebhookFormMode | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [reveal, setReveal] = useState<SecretReveal | null>(null);
  const [outcome, setOutcome] = useState<TestOutcome | null>(null);

  const load = () => {
    setState({ kind: "loading" });
    void loadable(() => loadWebhooks(client, context.orgId)).then(setState);
  };
  useEffect(load, [client, context.orgId]);

  const replaceRow = (webhook: NotificationWebhook) => {
    setState((current) => (current.kind === "loaded" ? { kind: "loaded", value: current.value.map((w) => (w.id === webhook.id ? webhook : w)) } : current));
    setOpen(webhookRow(webhook, me));
  };
  const showSecret = (answer: NotificationWebhookWithSecret) => {
    const { webhook, reveal: next } = revealSecret(answer);
    setReveal(next);
    return webhook;
  };
  const closeRow = () => {
    setOpen(null);
    setOutcome(null);
  };

  const run = (kind: Action, webhook: NotificationWebhook) => () => {
    switch (kind) {
      case "rotate":
        return rotateSecret(client, webhook).then((answer) => replaceRow(showSecret(answer)));
      case "test":
        return testWebhook(client, webhook).then((result) => {
          setOutcome(testOutcome(result));
          load();
        });
      case "toggle":
        return patchWebhook(client, webhook, { status: statusToggle(webhook).status }).then(replaceRow);
      case "delete":
        return deleteWebhook(client, webhook).then(() => {
          closeRow();
          load();
        });
    }
  };

  const webhooks = state.kind === "loaded" ? state.value : [];
  const writable = !readOnly && canWriteWebhooks(me) && state.kind === "loaded";
  const full = atLimit(webhooks);
  const newButton = writable && (
    <button type="button" class="button button-primary" disabled={full} title={full ? LIMIT_MESSAGE : undefined} onClick={() => setForm({ kind: "create" })}>
      New webhook
    </button>
  );

  return (
    <div class="stack">
      {readOnly && <StaleNote id="webhooks-stale-note" />}
      <Card title="Webhooks" help="notifications.webhooks" actions={newButton}>
        {full && <p class="banner banner-info">{LIMIT_MESSAGE}</p>}
        {state.kind === "unavailable" ? (
          <Empty message={NOT_AVAILABLE} />
        ) : (
          <DataTable
            caption="Webhooks"
            hideCaption
            columns={COLUMNS}
            rows={webhookRows(webhooks, me)}
            rowKey={(row) => row.id}
            loading={state.kind === "loading"}
            error={state.kind === "error" ? state.error : null}
            onRetry={load}
            emptyMessage="No webhooks yet — add one to hear in Slack or your own service when a quota warns, is reached or gets a grant."
            emptyAction={newButton}
            onRowClick={setOpen}
            rowLabel={(row) => `Open the webhook ${row.name}`}
          />
        )}
      </Card>
      {open && !action && !form && !reveal && (
        <RowDetail
          title={open.name}
          open
          onClose={closeRow}
          class="dialog-wide"
          facts={[
            { label: "Sends to", value: open.host },
            { label: "Format", value: open.format },
            { label: "Events", value: open.events },
            { label: "Status", value: <Chip tone={open.statusTone}>{open.statusLabel}</Chip> },
            { label: "Last success", value: open.lastSuccess },
            { label: "Last failure", value: open.lastFailure },
            { label: "Failures in a row", value: open.consecutiveFailures },
            ...(context.surface === "staff" ? [{ label: "Owner", value: open.ownerLabel }] : []),
          ]}
          actions={
            open.writable && !readOnly && (
              <>
                <button type="button" class="button button-danger" onClick={() => setAction("delete")}>
                  Delete
                </button>
                <button type="button" class="button button-secondary" onClick={() => setAction("toggle")}>
                  {statusToggle(open.webhook).label}
                </button>
                <button type="button" class="button button-secondary" onClick={() => setAction("rotate")}>
                  Rotate secret
                </button>
                <button type="button" class="button button-secondary" onClick={() => setAction("test")}>
                  Send test
                </button>
                <button type="button" class="button button-primary" onClick={() => setForm({ kind: "edit", webhook: open.webhook })}>
                  Edit
                </button>
              </>
            )
          }
        >
          {open.failing && <p class="banner banner-error">Deliveries have failed for 24 hours. Check the receiver, then send a test or re-enable it.</p>}
          {outcome && (
            <p class={outcome.delivered ? "banner banner-success" : "banner banner-error"} role="status">
              <strong>{outcome.headline}.</strong> {outcome.detail}
            </p>
          )}
          <h3>Deliveries, last 30 days</h3>
          <DeliveriesList webhook={open.webhook} />
        </RowDetail>
      )}
      {open && action && (
        <ConfirmedAction
          title={`${action === "toggle" ? statusToggle(open.webhook).label : ACTION_TEXT[action].label} — ${open.name}?`}
          message={<p>{action === "toggle" ? toggleMessage(open.webhook) : ACTION_TEXT[action].message}</p>}
          confirmLabel={action === "toggle" ? statusToggle(open.webhook).label : ACTION_TEXT[action].label}
          typedName={action === "delete" ? open.name : undefined}
          run={run(action, open.webhook)}
          onDone={() => setAction(null)}
          onClose={() => setAction(null)}
        />
      )}
      {form && (
        <WebhookForm
          context={context}
          mode={form}
          owners={ownerChoices(context.surface, me)}
          onClose={() => setForm(null)}
          onCreated={(answer) => {
            showSecret(answer);
            setForm(null);
            load();
          }}
          onSaved={(webhook) => {
            setForm(null);
            replaceRow(webhook);
          }}
        />
      )}
      {reveal && <SecretRevealDialog reveal={reveal} onChange={setReveal} onClose={() => setReveal(null)} />}
    </div>
  );
}
